import { RendererOptions } from '../types'

import * as THREE from 'three'

import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'
import { HueSaturationShader } from 'three/examples/jsm/shaders/HueSaturationShader.js'
import { BrightnessContrastShader } from 'three/examples/jsm/shaders/BrightnessContrastShader.js'

/**
 * Setup post-processing effects
 */
export const setupPostProcessor = (
  renderer: THREE.WebGLRenderer,
  camera: THREE.Camera,
  scene: THREE.Scene,
  options: RendererOptions
): EffectComposer => {
  const antialiasing = options.antialiasing ?? true
  const brightness = options.brightness ?? 0
  const saturation = options.saturation ?? 0
  const contrast = options.contrast ?? 0
  const bloom = options.bloom || { strength: 0, radius: 0, threshold: 0 }

  const pixelRatio = renderer.getPixelRatio()

  const composer = new EffectComposer(renderer)
  composer.addPass(new RenderPass(scene, camera))

  // Bloom Effect
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    bloom.strength,
    bloom.radius,
    bloom.threshold
  )
  composer.addPass(bloomPass)

  // Hue/Saturation
  const hueSatPass = new ShaderPass(HueSaturationShader)
  hueSatPass.uniforms['saturation'].value = saturation
  composer.addPass(hueSatPass)

  // Brightness/Contrast
  const contrastPass = new ShaderPass(BrightnessContrastShader)
  contrastPass.uniforms['brightness'].value = brightness
  contrastPass.uniforms['contrast'].value = contrast
  composer.addPass(contrastPass)

  // Antialiasing
  if (antialiasing) {
    const fxaaPass = new ShaderPass(FXAAShader)
    const uniforms = fxaaPass.material.uniforms
    uniforms['resolution'].value.x = 1 / (window.innerWidth * pixelRatio)
    uniforms['resolution'].value.y = 1 / (window.innerHeight * pixelRatio)
    composer.addPass(fxaaPass)
  }

  return composer
}
